import { formatBRL, formatDate, formatDateTime } from "@/lib/api";
import { datasVenda } from "@/components/fiscal/NfLinha";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Ban, History, Receipt, Mail, MessageSquareWarning } from "lucide-react";
import { RevisoesLista } from "@/components/fiscal/RevisaoNf";

const ACOES = {
  solicitacao_cancelada: "Solicitação cancelada",
  nf_cancelada: "Cancelamento da NF registrado",
  cancelamento_desfeito: "Cancelamento desfeito · voltou para Pendente",
  cancelamento_pedido: "Cancelamento pedido",
  inscricao_unificada: "Inscrição unificada (novas pessoas do mesmo município)",
  nf_substituida: "NF anterior cancelada e substituída",
};

function Bloco({ titulo, icon: Ico, cls = "border-slate-200 bg-white", children, testid }) {
  return (
    <div className={`rounded-xl border p-3 space-y-1 text-sm ${cls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1">
        <Ico className="w-3.5 h-3.5" /> {titulo}
      </div>
      {children}
    </div>
  );
}

function CancelInfo({ ins }) {
  return (
    <Bloco titulo={ins.nf_cancel_tipo === "nf_emitida" ? "NF cancelada" : "Solicitação cancelada"} icon={Ban} cls="border-red-200 bg-red-50" testid="hist-cancel-info">
      <div><b>Motivo:</b> {ins.nf_cancel_motivo}</div>
      <div><b>Por:</b> {ins.nf_cancelada_por_nome} · {formatDateTime(ins.nf_cancelada_em)}</div>
      {ins.nf_cancel_protocolo && <div><b>Protocolo:</b> {ins.nf_cancel_protocolo} · cancelada em {formatDate(ins.nf_cancel_data)}</div>}
    </Bloco>
  );
}

function Timeline({ eventos }) {
  if (!eventos.length) return <div className="text-sm text-slate-400">Nenhum cancelamento registrado.</div>;
  return (
    <ol className="space-y-2" data-testid="hist-timeline">
      {eventos.map((e, idx) => (
        <li key={`${e.em}-${idx}`} className="border-l-2 border-slate-300 pl-3 text-sm">
          <div className="font-semibold text-slate-800">{ACOES[e.acao] || e.acao}</div>
          <div className="text-xs text-slate-500">{e.por_nome} · {formatDateTime(e.em)}</div>
          {e.motivo && <div className="text-xs text-slate-600">Motivo: {e.motivo}</div>}
          {e.protocolo && <div className="text-xs text-slate-600">Protocolo: {e.protocolo} · {formatDate(e.data)}</div>}
        </li>
      ))}
    </ol>
  );
}

export function HistoricoNfModal({ ins, onClose }) {
  const eventos = [...(ins?.nf_historico || [])].reverse();
  return (
    <Dialog open={!!ins} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="modal-historico-nf">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-slate-600" /> Histórico da NF</DialogTitle>
          <DialogDescription>
            {ins?.municipio_nome}{ins?.municipio_uf ? ` / ${ins.municipio_uf}` : ""} · {formatBRL(ins?.valor_vendido || 0)} · {ins?.vendedora_nome}
          </DialogDescription>
        </DialogHeader>
        {ins && (
          <div className="space-y-3">
            <div className="text-xs text-slate-500">{ins.curso_rotulo || ins.curso_nome} · {datasVenda(ins).length > 1 ? "vendas em" : "venda em"} {datasVenda(ins).map(formatDate).join(", ")}</div>
            {ins.nf_status === "cancelada" && <CancelInfo ins={ins} />}
            {ins.nf_cancelamento_pedido && ins.nf_status !== "cancelada" && (
              <Bloco titulo="Cancelamento pedido" icon={Ban} cls="border-orange-200 bg-orange-50" testid="hist-pedido">
                <div>{ins.nf_cancelamento_pedido.por_nome} · {formatDateTime(ins.nf_cancelamento_pedido.em)}</div>
                <div><b>Motivo:</b> {ins.nf_cancelamento_pedido.motivo}</div>
              </Bloco>
            )}
            {ins.nf_substituir?.length > 0 && (
              <Bloco titulo="NF a cancelar (inscrição unificada)" icon={Ban} cls="border-red-200 bg-red-50" testid="hist-substituir">
                {ins.nf_substituir.map((s) => <div key={s.nf_numero}>NF nº <b>{s.nf_numero}</b> · {formatBRL(s.valor || 0)} · aguardando cancelamento e nova emissão</div>)}
              </Bloco>
            )}
            {ins.nf_anteriores?.length > 0 && (
              <Bloco titulo="NFs anteriores canceladas" icon={Receipt} testid="hist-anteriores">
                {ins.nf_anteriores.map((s) => <div key={`${s.nf_numero}-${s.cancelada_em}`}>NF nº <b>{s.nf_numero}</b> · protocolo {s.protocolo_cancelamento} · {s.cancelada_por_nome} · {formatDateTime(s.cancelada_em)}</div>)}
              </Bloco>
            )}
            {ins.nf_numero && (
              <Bloco titulo="Nota emitida" icon={Receipt} testid="hist-emissao">
                <div>NF nº <b>{ins.nf_numero}</b> · emitida em {formatDate(ins.nf_data_emissao)}</div>
              </Bloco>
            )}
            {ins.nf_revisoes?.length > 0 && (
              <Bloco titulo="Solicitações de revisão" icon={MessageSquareWarning} testid="hist-revisoes">
                <RevisoesLista ins={ins} />
              </Bloco>
            )}
            {ins.nf_responsavel_email && (
              <Bloco titulo="Responsável pela NF" icon={Mail} testid="hist-responsavel">
                <div>{ins.nf_responsavel_nome} · {ins.nf_responsavel_email}</div>
              </Bloco>
            )}
            <Timeline eventos={eventos} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
